import {reporter} from 'common/report/error-reporting';

import {
  OmnisearchResult,
  OmnisearchResultType,
  TrackClickOptions,
  OmnisearchResultViewElements,
  ProjectOrWorkspace,
  ResultOrder,
} from './types';
import {trackResultClick} from './tracking';

const waitFor = (msDelay: number) => new Promise((resolve) => setTimeout(resolve, msDelay));

// The order specified is based on usage as of Dec 2019. Order was intentionally chosen to be 10 apart to
// allow for future reordering without updating all values.
// prettier-ignore
const resultTypeToViewMap = new Map<OmnisearchResultType, OmnisearchResultViewElements>([
  [
    OmnisearchResultType.Campaign,
    {icon: `sent`, name: `Campaign`, plural: `Campaigns`, order: ResultOrder.Campaign},
  ],
  [
    OmnisearchResultType.Cohort,
    {icon: `community`, name: `Cohort`, plural: `Cohorts`, order: ResultOrder.Cohort},
  ],
  [
    OmnisearchResultType.Dashboard,
    {icon: `dashboard`, name: `Dashboard`, plural: `Dashboards`, order: ResultOrder.Dashboard},
  ],
  [
    OmnisearchResultType.Event,
    {icon: `event`, name: `Event`, plural: `Events`, order: ResultOrder.Event},
  ],
  [
    OmnisearchResultType.Message,
    {icon: `email`, name: `Message`, plural: `Messages`, order: ResultOrder.Message},
  ],
  [
    OmnisearchResultType.Report,
    {icon: `report`, name: `Report`, plural: `Reports`, order: ResultOrder.Report},
  ],
  [
    OmnisearchResultType.Help,
    {icon: `help-center`, name: `Help Center`, plural: `Help Center`, order: ResultOrder.Help},
  ],
]);

export function track(event: string, props = {}) {
  return new Promise((resolve) => mixpanel.track(`[Omnisearch] ${event}`, props, resolve));
}

export function getTypeIcon(resultType: OmnisearchResultType) {
  if (resultTypeToViewMap.has(resultType)) {
    return resultTypeToViewMap.get(resultType).icon;
  }

  reporter.error(`unexpected result type ${resultType}`);
  return `link`;
}

export function getTypeText(resultType: OmnisearchResultType) {
  if (resultTypeToViewMap.has(resultType)) {
    return resultTypeToViewMap.get(resultType).name;
  }

  reporter.error(`unexpected result type ${resultType}`);
  return `Unknown`;
}

export function getTypeKey(resultType: OmnisearchResultType | `all`) {
  if (resultType === `all`) {
    return `all`;
  }
  if (resultTypeToViewMap.has(resultType)) {
    return resultTypeToViewMap.get(resultType).name.toLowerCase();
  }

  reporter.error(`unexpected result type ${resultType}`);
  return `unknown`;
}

export function getTypeTextPlural(resultType: OmnisearchResultType) {
  if (resultTypeToViewMap.has(resultType)) {
    return resultTypeToViewMap.get(resultType).plural;
  }

  console.warn(`unexpected result type`, resultType);
  return `Unknown`;
}

export function getTypeTextFromCount(resultType: OmnisearchResultType, count: number) {
  if (resultTypeToViewMap.has(resultType)) {
    return count === 1 ? resultTypeToViewMap.get(resultType).name : resultTypeToViewMap.get(resultType).plural;
  }

  console.warn(`unexpected result type`, resultType);
  return `Unknown`;
}

export function getSupportedTypesEnums(types: string[]): OmnisearchResultType[] {
  const supportedTypes: OmnisearchResultType[] = [];
  for (const [type, elements] of resultTypeToViewMap.entries()) {
    if (types.includes(elements.name.toLowerCase())) {
      supportedTypes.push(type);
    }
  }

  return supportedTypes.sort((a, b) => resultTypeToViewMap.get(a).order - resultTypeToViewMap.get(b).order);
}

export function trackAndNavigate(
  item: OmnisearchResult,
  index: number,
  {openInNewWindow, openInCurrentWindow, selectedType, count}: TrackClickOptions,
) {
  const navigate = () => {
    if (openInCurrentWindow) {
      return window.location.assign(item.link);
    } else if (openInNewWindow) {
      return window.open(item.link);
    }
  };

  Promise.race([
    trackResultClick({
      ...item,
      index,
      selectedType,
      count,
    }),
    waitFor(100),
  ]).then(navigate, navigate);
}

function getStorageFacetsKey({projectId, workspaceId}: ProjectOrWorkspace) {
  return workspaceId ? `search.workspace-${workspaceId}.facets` : `search.${projectId}.facets`;
}

export function saveFacets({projectId, workspaceId}: ProjectOrWorkspace, facets: string[]) {
  window.localStorage.setItem(getStorageFacetsKey({projectId, workspaceId}), JSON.stringify(facets));
  return facets;
}

export function getFacets({projectId, workspaceId}: ProjectOrWorkspace) {
  const facets = window.localStorage.getItem(getStorageFacetsKey({projectId, workspaceId}));
  return facets ? JSON.parse(facets) : [];
}
